import {
  Access,
  AccessRule,
  AccessSource,
  Department,
  EmployeeSubtype,
  EmployeeType,
  Prisma,
  User,
} from '@prisma/client';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import {
  AccessRuleWithDetail,
  CreateAccessRuleDto,
  DOMAIN_ACCESS_RULE_PATH,
  DOMAIN_ACCESS_URL_ROOT,
  EnsureAccessDto,
  ExceptionDto,
} from '@app/domain-access-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

import { accessKeys } from '../../access/data';

type ConflictDetails = {
  access: Access;
  employeeTypes: EmployeeType;
  employeeSubtype?: EmployeeSubtype;
  createdByUser: User;
  updatedByUser: User;
};

export type AccessConflict = AccessRule &
  ConflictDetails & {
    conflictAccess: Access;
  };
export type DepartmentConflict = AccessRule &
  ConflictDetails & {
    department: Department;
  };

export const accessRulesKeys = {
  all: ['access-rules'] as const,
  lists: () => [...accessRulesKeys.all, 'list'] as const,
  list: (filters: object) => [...accessRulesKeys.lists(), filters] as const,
  item: (id: number) => [...accessRulesKeys.all, 'item', id],
};

export const useFindAccessRules = <T>(
  args: Prisma.AccessRuleFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: accessRulesKeys.list(args),
    queryFn: () =>
      axios.post(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/find`, args).then((response) => response.data),
  });
};

type CreateAccessRule = {
  employeeType: EmployeeType;
  employeeSubtype?: EmployeeSubtype;
  owners: string[];
  exceptions: ExceptionDto[];
  externalId?: string;
  accessSource?: AccessSource;
  departmentId?: string;
};

export const useCreateAccessRule = (accessId: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccessRule>, AxiosError, CreateAccessRule>({
    mutationFn: (conflict) => axios.post(`/${DOMAIN_ACCESS_URL_ROOT}/${accessId}/${DOMAIN_ACCESS_RULE_PATH}`, conflict),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessRulesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.accessId) });
      response.data.conflictAccessId &&
        queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.conflictAccessId) });
    },
  });
};

export const useGetAccessRule = (
  id: number,
  options?: Omit<UseQueryOptions<AccessRuleWithDetail>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<AccessRuleWithDetail>({
    ...options,
    queryKey: accessRulesKeys.item(id),
    queryFn: () =>
      axios.get(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/${id}`).then((response) => response.data),
  });
};

type PromoteAccessRule = { access: EnsureAccessDto } & CreateAccessRule;

export const usePromoteAccessRule = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccessRule>, AxiosError, PromoteAccessRule>({
    mutationFn: (body) => axios.post(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}`, body),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessRulesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.accessId) });
      response.data.conflictAccessId &&
        queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.conflictAccessId) });
    },
  });
};

export const useEditAccessRule = (conflictId: number) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccessRule>, unknown, CreateAccessRuleDto>({
    mutationFn: (body) => axios.patch(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/${conflictId}`, body),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessRulesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.accessId) });
      response.data.conflictAccessId &&
        queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data.conflictAccessId) });
    },
  });
};

export const useDeleteAccessRule = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccessRule[]>, AxiosError, number>({
    mutationFn: (conflictId) => axios.delete(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/${conflictId}`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: accessKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessRulesKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data?.[0].accessId) });
      response.data?.[0].conflictAccessId &&
        queryClient.invalidateQueries({ queryKey: accessKeys.item(response.data?.[0].conflictAccessId) });
    },
  });
};
